import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { redirectToOAuth } from "../../../../utils/safeUrl";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft } from "lucide-react";
import { defaultActiveQuestions } from "../../screening/QuestionsBuilder";
import DiscardChangesModal from "../../../../components/shared/DiscardChangesModal";
import Loader from "../../../../components/Loader";
import { useNavigationGuard } from "../../../../hooks/useDiscardGuard";
import { useTheme } from "../../../../hooks/useTheme";
import "./gradientAccents.css";
import EmailTestModal from "../EmailTestModal";
import SequenceStepIndicator from "./SequenceStepIndicator";
import StepCreate from "./steps/StepCreate";
import StepQuestions from "./steps/StepQuestions";
import StepSequence from "./steps/StepSequence";
import StepReview from "./steps/StepReview";
import { useSequenceDraft } from "./useSequenceDraft";
import {
    blankStep,
    canLeaveSequence,
    connectMailbox,
    createSequence,
    fetchGmailAccount,
    fetchSequenceForEdit,
    hasFormLink,
    isEmailStep,
    normalizeDelays,
    questionsBlocker,
    updateSequence,
    validateSequence,
} from "./sequenceBuilderApi";

const SEQ_LIST = "/org-admin/sourcing/sequences";

// Step 1 names the sequence and picks its type; that choice decides whether Questions Setup
// exists at all, so the two paths diverge from step 2 onwards.
const WIZARD_STEPS = {
    screening: [
        { key: "create", label: "Create Outreach" },
        { key: "questions", label: "Questions Setup" },
        { key: "sequence", label: "Outreach" },
        { key: "review", label: "Review & Launch" },
    ],
    plain: [
        { key: "create", label: "Create Outreach" },
        { key: "sequence", label: "Outreach" },
        { key: "review", label: "Review & Launch" },
    ],
};

// The forward button names where it goes rather than saying "Next", so on step 1 the
// screening toggle visibly changes what happens when you press it — with screening on the
// next stop is the questions, with it off you land straight in the email cadence.
const NEXT_STEP_LABEL = {
    questions: "Set Up Screening",
    sequence: "Configure Outreach",
    review: "Review & Launch",
};

const makeInitialForm = () => ({
    // Empty so step 1's field shows its placeholder — the user names the sequence before
    // anything else, the way the job-role flow asks for a job title first.
    name: "",
    // Off by default — a plain email cadence is the smaller ask; screening is opt-in.
    includeScreening: false,
    questions: defaultActiveQuestions(),
    currency: "INR",
    startTiming: "immediately",
    steps: [blankStep(0)],
});

/**
 * Wizard footer nav, mirroring the job-role flow's NavigationButtons — same gradient `.button`
 * pill, same `justify-between` row inside the content column rather than a separate footer bar.
 */
const NavigationButtons = ({ onBack, showBackButton = true, children, onNext, nextDisabled, nextLabel, theme }) => (
    <div className="flex justify-between items-center w-full py-4">
        {showBackButton ? (
            <button
                onClick={onBack}
                className={`${theme !== "dark" ? "border-btn !text-textSecondary" : "button"} flex items-center gap-2 rounded-full dark:!text-white dark:border dark:border-gray-600 hover:border-gray-400 transition-all duration-200`}
            >
                <span>Back</span>
            </button>
        ) : (
            <div />
        )}
        <div className="flex items-center gap-3">
            {children}
            <button
                onClick={onNext}
                disabled={nextDisabled}
                className={`button px-6 py-3 rounded-full text-white ${nextDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
                <span>{nextLabel}</span>
            </button>
        </div>
    </div>
);

/**
 * Single-page, multi-step wizard for creating or editing a reusable outreach sequence — email,
 * WhatsApp and AI call steps, one channel per step. Only an outreach with a combined Email +
 * WhatsApp step (a classic-modal leftover) still edits in SequenceCreatorModal; SequenceManager
 * decides which. Three entry points: standalone create (from the sequence list,
 * no router state), edit (URL has :id, this component fetches its own data), and create-from-
 * enrollment — the latter arrives with { candidateIds, jobId, returnTo } in router state and, on
 * save, hands back to that flow to start outreach.
 */
const SequenceBuilder = () => {
    const navigate = useNavigate();
    const { state: navState } = useLocation();
    const { id: sequenceId } = useParams();
    const isEditMode = !!sequenceId;
    // returnTo is set when the wizard is opened from the enrollment flow, so we land the user back
    // on their candidate page (with the new sequence created) instead of the sequences list.
    const { returnTo = null } = navState || {};

    const orgId = useSelector((s) => s.userSlice?.org_id);
    const recruiterId = useSelector((s) => s.userSlice?.org_admin_id);
    // Signs AI-drafted emails. The store is empty on a cold reload until the profile fetch
    // lands, which is what leaves the sign-off as a bare "Best," — so fall back to the cached
    // username the rest of sourcing already reads.
    const recruiterName =
        useSelector((s) => s.userSlice?.name) || localStorage.getItem("username") || "";
    const { theme } = useTheme();

    const [form, setForm] = useState(() => (isEditMode ? null : makeInitialForm()));
    const [currentStep, setCurrentStep] = useState(1);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [editError, setEditError] = useState(null);
    const [gmailEmail, setGmailEmail] = useState(null);
    const [accountLoading, setAccountLoading] = useState(true);
    const [previewStep, setPreviewStep] = useState(null);
    const [discardOpen, setDiscardOpen] = useState(false);
    // Whether the screening choice is still flippable — false only once candidates are enrolled.
    // Starts locked so a restored draft can't offer the toggle before the server has answered,
    // and stays locked if that fetch fails. Irrelevant in create mode (see typeLocked below).
    const [screeningLocked, setScreeningLocked] = useState(true);

    const dirtyRef = useRef(dirty);
    dirtyRef.current = dirty;
    const pendingDest = useRef(SEQ_LIST);
    // Populated once the edit fetch resolves; needed by handleSubmit's updateSequence call, not
    // part of the draft snapshot (it's server-derived, not user-edited).
    const editMetaRef = useRef({ formId: null, originalStepIds: [], originalIncludeScreening: false });
    // Guards the edit fetch from clobbering a fresher local draft that already restored.
    const restoredFromDraftRef = useRef(false);

    const wizardSteps = WIZARD_STEPS[form?.includeScreening === false ? "plain" : "screening"];
    const activeKey = wizardSteps[currentStep - 1]?.key;

    // ── Draft (refresh recovery) ────────────────────────────────────────────────
    const snapshot = useMemo(() => ({ form, currentStep }), [form, currentStep]);
    const onRestore = useCallback((saved) => {
        restoredFromDraftRef.current = true;
        setForm(saved.form);
        setCurrentStep(saved.currentStep || 1);
        setDirty(true);
    }, []);
    const draft = useSequenceDraft({
        orgId,
        recruiterId,
        scopeId: sequenceId || "new",
        dirty,
        snapshot,
        onRestore,
    });

    // ── Load the sequence being edited ──────────────────────────────────────────
    useEffect(() => {
        if (!isEditMode || !orgId) return undefined;
        let cancelled = false;
        fetchSequenceForEdit(sequenceId, { orgId, recruiterId })
            .then(({ form: fetched, formId, originalStepIds, originalIncludeScreening, typeLocked }) => {
                if (cancelled) return;
                editMetaRef.current = { formId, originalStepIds, originalIncludeScreening };
                setScreeningLocked(typeLocked);
                // A restored draft keeps the step it was left on; otherwise an edit opens on
                // Review, which shows the whole outreach and links each section to its step —
                // the recruiter came to change one thing, not to walk the wizard again.
                if (!restoredFromDraftRef.current) {
                    setForm(fetched);
                    setCurrentStep(WIZARD_STEPS[fetched.includeScreening ? "screening" : "plain"].length);
                }
            })
            .catch((e) => {
                if (!cancelled) setEditError(e.message || "Failed to load this outreach.");
            });
        return () => {
            cancelled = true;
        };
    }, [isEditMode, sequenceId, orgId, recruiterId]);

    // Keep the active step in range if screening is toggled off from a later step count.
    useEffect(() => {
        setCurrentStep((s) => Math.min(s, wizardSteps.length));
    }, [wizardSteps.length]);

    // ── Gmail sender status ─────────────────────────────────────────────────────
    useEffect(() => {
        if (!orgId || !recruiterId) return undefined;
        const controller = new AbortController();
        setAccountLoading(true);
        fetchGmailAccount({ orgId, recruiterId, signal: controller.signal })
            .then((email) => setGmailEmail(email))
            .catch(() => { if (!controller.signal.aborted) setGmailEmail(null); })
            .finally(() => { if (!controller.signal.aborted) setAccountLoading(false); });
        return () => controller.abort();
    }, [orgId, recruiterId]);

    // ── Form mutations ──────────────────────────────────────────────────────────
    const patchForm = useCallback((updates) => {
        setDirty(true);
        setForm((prev) => ({ ...prev, ...updates }));
    }, []);

    const updateSteps = useCallback((fn) => {
        setDirty(true);
        setForm((prev) => ({ ...prev, steps: fn(prev.steps) }));
    }, []);

    const updateStep = useCallback((idx, field, value) => {
        updateSteps((steps) => {
            const next = steps.map((s, i) => (i === idx ? { ...s, [field]: value } : s));
            return field === "delayDays" ? normalizeDelays(next) : next;
        });
    }, [updateSteps]);

    const addStep = useCallback((channel = "email") => {
        updateSteps((steps) =>
            normalizeDelays([
                ...steps,
                { ...blankStep((steps[steps.length - 1]?.delayDays ?? 0) + 1), channel },
            ])
        );
    }, [updateSteps]);

    const insertStepAt = useCallback((idx) => {
        updateSteps((steps) => {
            const next = [...steps];
            next.splice(idx, 0, blankStep(0));
            return normalizeDelays(next);
        });
    }, [updateSteps]);

    const deleteStep = useCallback((idx) => {
        updateSteps((steps) => (steps.length <= 1 ? steps : normalizeDelays(steps.filter((_, i) => i !== idx))));
    }, [updateSteps]);

    const reorderSteps = useCallback((from, to) => {
        updateSteps((steps) => {
            const next = [...steps];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return normalizeDelays(next);
        });
    }, [updateSteps]);

    const toggleScreening = useCallback((checked) => {
        setDirty(true);
        setForm((prev) =>
            checked
                ? {
                      ...prev,
                      includeScreening: true,
                      questions: prev.questions.length ? prev.questions : defaultActiveQuestions(),
                  }
                : { ...prev, includeScreening: false }
        );
    }, []);

    // ── Navigation / exit ───────────────────────────────────────────────────────
    const [formLinkWarning, setFormLinkWarning] = useState(false);
    const goNext = () => setCurrentStep((s) => Math.min(s + 1, wizardSteps.length));
    const goBack = () => setCurrentStep((s) => Math.max(s - 1, 1));
    const goToStep = useCallback((key) => {
        setCurrentStep((s) => {
            const idx = wizardSteps.findIndex((step) => step.key === key);
            return idx === -1 ? s : idx + 1;
        });
    }, [wizardSteps]);

    const attemptLeave = useCallback((dest) => {
        if (dirtyRef.current) {
            pendingDest.current = dest;
            setDiscardOpen(true);
        } else {
            navigate(dest);
        }
    }, [navigate]);

    const handleDiscard = () => {
        draft.clear();
        setDirty(false);
        dirtyRef.current = false;
        setDiscardOpen(false);
        navigate(pendingDest.current || SEQ_LIST);
    };

    useNavigationGuard(() => attemptLeave(returnTo || SEQ_LIST));

    const handleConnect = async () => {
        try {
            redirectToOAuth(await connectMailbox({ orgId, recruiterId }));
        } catch {
            setError("Failed to start Gmail connection. Please try again.");
        }
    };

    const handleSubmit = async () => {
        const message = validateSequence(form);
        if (message) {
            setError(message);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            if (isEditMode) {
                const saved = await updateSequence(form, {
                    orgId,
                    recruiterId,
                    sequenceId,
                    formId: editMetaRef.current.formId,
                    originalStepIds: editMetaRef.current.originalStepIds,
                    originalIncludeScreening: editMetaRef.current.originalIncludeScreening,
                });
                editMetaRef.current = {
                    ...editMetaRef.current,
                    formId: saved.formId,
                    originalIncludeScreening: saved.includeScreening,
                };
            } else {
                await createSequence(form, { orgId, recruiterId });
            }
            draft.clear();
            setDirty(false);
            dirtyRef.current = false;
            if (isEditMode) navigate(SEQ_LIST, { state: { toast: "Outreach updated successfully" } });
            else if (returnTo) navigate(returnTo);
            else navigate(SEQ_LIST, { state: { toast: "Outreach created successfully" } });
        } catch (e) {
            setError(e.response?.data?.detail || e.message || `Failed to ${isEditMode ? "update" : "create"} outreach.`);
            setSaving(false);
        }
    };

    // Held as the reason rather than a boolean so the disabled Next button can say why.
    const questionsBlock = form && activeKey === "questions" ? questionsBlocker(form) : null;
    const canNext = !form
        ? false
        : activeKey === "create" ? !!form.name.trim()
        : activeKey === "questions" ? !questionsBlock
        : activeKey === "sequence" ? canLeaveSequence(form)
        : true;
    // currentStep is 1-based, so this index is the step the button leads to. Recomputed with
    // wizardSteps, which means toggling screening on step 1 relabels it straight away.
    const nextLabel = NEXT_STEP_LABEL[wizardSteps[currentStep]?.key] || "Next";
    const fromEnrollment = !!returnTo;

    // Only an email step can carry {{form_link}}, so the warning is meaningless for an outreach
    // that reaches the candidate purely by call or WhatsApp — the agent asks the questions aloud
    // and a WhatsApp step sends a fixed approved template.
    const needsFormLink = (f) =>
        f.includeScreening && f.steps.some(isEmailStep) && !hasFormLink(f.steps);

    const handleNext = () => {
        if (!form) return;
        if (activeKey === "sequence" && needsFormLink(form)) {
            setFormLinkWarning(true);
            return;
        }
        setFormLinkWarning(false);
        goNext();
    };
    useEffect(() => {
        if (formLinkWarning && form && !needsFormLink(form)) setFormLinkWarning(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form?.steps, form?.includeScreening]);

    if (editError) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-3">
                <p className="text-sm text-red-500 dark:text-red-400 max-w-md text-center">{editError}</p>
                <button onClick={() => navigate(SEQ_LIST)} className="text-sm seq-gradient-text hover:underline">
                    Back to outreach
                </button>
            </div>
        );
    }

    if (!form) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader />
            </div>
        );
    }

    const submitLabel = saving
        ? isEditMode
            ? "Updating…"
            : "Creating…"
        : isEditMode
          ? "Update outreach"
          : fromEnrollment
            ? "Create & continue"
            : "Create outreach";

    return (
        <div className="min-h-screen">
            {/* Same container contract as the Create JD wizard: an opaque light fill, and
                nothing at all in dark so the shell's background image shows through. The
                explicit dark:bg-none matters — bgPrimary's token is only defined under
                .light, so dark was relying on an unresolvable var falling back to
                transparent rather than saying what it wanted. */}
            <div className="flex flex-col min-h-screen border border-gray-700/50 rounded-2xl pt-4 bg-bgPrimary dark:bg-none">
                {/* Header — mirrors the job-role wizard: back arrow + mode label on the left. */}
                <div className="flex items-center justify-between px-6 pt-2">
                    <div className="flex items-center">
                        <button
                            onClick={() => attemptLeave(returnTo || SEQ_LIST)}
                            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                            title={fromEnrollment ? "Back" : "Back to outreach"}
                        >
                            <ArrowLeft className="w-5 h-5 cursor-pointer dark:text-[#C9A3FF] text-textSecondary" />
                        </button>
                        <span className="dark:text-gray-400 text-textSecondary">
                            {isEditMode ? "Edit Outreach" : "New Outreach"}
                        </span>
                    </div>
                </div>

                <SequenceStepIndicator steps={wizardSteps} current={currentStep} onStepClick={setCurrentStep} />

                {/* Content */}
                <div className="px-4 mt-4 flex-1 flex flex-col">
                    <div className="w-full px-12 space-y-6 flex-1 flex flex-col">
                        {error && (
                            <p className="text-[13px] text-red-500 dark:text-red-400 dark:bg-red-500/10 bg-red-50 dark:border-red-500/30 border border-red-200 rounded-lg px-3 py-2">
                                {error}
                            </p>
                        )}

                        {activeKey === "create" && (
                            <StepCreate
                                name={form.name}
                                includeScreening={form.includeScreening}
                                onNameChange={(v) => patchForm({ name: v })}
                                onTypeChange={toggleScreening}
                                typeLocked={isEditMode && screeningLocked}
                            />
                        )}
                        {activeKey === "questions" && (
                            <StepQuestions
                                questions={form.questions}
                                currency={form.currency}
                                onQuestionsChange={(q) => patchForm({ questions: q })}
                                onCurrencyChange={(c) => patchForm({ currency: c })}
                            />
                        )}
                        {activeKey === "sequence" && (
                            <StepSequence
                                steps={form.steps}
                                onUpdateStep={updateStep}
                                onAddStep={addStep}
                                onInsertAt={insertStepAt}
                                onDeleteStep={deleteStep}
                                onReorder={reorderSteps}
                                startTiming={form.startTiming}
                                onStartTimingChange={(t) => patchForm({ startTiming: t })}
                                gmailEmail={gmailEmail}
                                accountLoading={accountLoading}
                                onConnectMailbox={handleConnect}
                                includeScreening={form.includeScreening}
                                // Turning screening on inserts a "Questions Setup" step ahead of
                                // this one, so hold position by index or the user is silently
                                // bounced onto the questions screen mid-edit.
                                onEnableScreening={() => {
                                    toggleScreening(true);
                                    setCurrentStep((s) => s + 1);
                                }}
                                onTestStep={setPreviewStep}
                                outreachName={form.name}
                                senderName={recruiterName}
                            />
                        )}
                        {activeKey === "review" && (
                            <StepReview form={form} gmailEmail={gmailEmail} onTestStep={setPreviewStep} onGoToStep={goToStep} />
                        )}

                        {/* Footer nav lives inside the content column, as it does in the job-role wizard. */}
                        {activeKey === "review" ? (
                            <NavigationButtons
                                theme={theme}
                                onBack={goBack}
                                showBackButton={currentStep > 1}
                                onNext={handleSubmit}
                                nextDisabled={saving}
                                nextLabel={submitLabel}
                            />
                        ) : (
                            <NavigationButtons
                                theme={theme}
                                onBack={goBack}
                                showBackButton={currentStep > 1}
                                onNext={handleNext}
                                nextDisabled={!canNext}
                                nextLabel={nextLabel}
                            >
                                {formLinkWarning && (
                                    <p className="text-xs text-amber-600 dark:text-amber-300">
                                        Screening included — form link not included in any step.
                                    </p>
                                )}
                                {activeKey === "questions" && questionsBlock && (
                                    <p className="text-xs text-amber-600 dark:text-amber-300 max-w-md text-right">{questionsBlock}</p>
                                )}
                            </NavigationButtons>
                        )}
                    </div>
                </div>
            </div>

            <EmailTestModal
                isOpen={!!previewStep}
                onClose={() => setPreviewStep(null)}
                step={previewStep}
                gmailEmail={gmailEmail}
                orgId={orgId}
                recruiterId={recruiterId}
            />
            <DiscardChangesModal isOpen={discardOpen} onCancel={() => setDiscardOpen(false)} onConfirm={handleDiscard} />
        </div>
    );
};

export default SequenceBuilder;
